import { Plugin, icons } from '@ckeditor/ckeditor5-core';
import {
    ButtonView,
    DropdownButtonView,
    Model,
    createDropdown,
    addListToDropdown
} from '@ckeditor/ckeditor5-ui';
import { CKEditorError, Collection } from '@ckeditor/ckeditor5-utils';
import RecorderResizeEditing from './recorderresizeediting';

const RESIZE_ICONS = {
    small: icons.objectSizeSmall,
    medium: icons.objectSizeMedium,
    large: icons.objectSizeLarge,
    original: icons.objectSizeFull
};

export default class RecorderResizeButtons extends Plugin {
    static get requires() {
        return [ RecorderResizeEditing ];
    }

    static get pluginName() {
        return 'RecorderResizeButtons';
    }

    constructor( editor ) {
        super( editor );
        this._resizeUnit = editor.config.get( 'recorder.resizeUnit' ) || '%';
    }

    init() {
        const editor = this.editor;
        const options = editor.config.get( 'recorder.resizeOptions' );
        const command = editor.commands.get( 'resizeRecorder' );

        this.bind( 'isEnabled' ).to( command );

        // Botones individuales
        for ( const option of options ) {
            this._registerRecorderResizeButton( option );
        }

        // Dropdown
        this._registerRecorderResizeDropdown( options );
    }

    _registerRecorderResizeButton( option ) {
        const editor = this.editor;
        const { name, value, icon } = option;

        // Ya NO añadimos la unidad aquí
        const optionValue = value ? value : null;

        editor.ui.componentFactory.add( name, locale => {
            const button = new ButtonView( locale );
            const command = editor.commands.get( 'resizeRecorder' );
            const labelText = this._getOptionLabelValue( option, true );

            if ( !RESIZE_ICONS[ icon ] ) {
                throw new CKEditorError( 'recorderresizebuttons-missing-icon', editor, option );
            }

            button.set( {
                label: labelText,
                icon: RESIZE_ICONS[ icon ],
                tooltip: labelText,
                isToggleable: true
            } );

            button.bind( 'isEnabled' ).to( this );
            button.bind( 'isOn' ).to( command, 'value', getIsOnButtonCallback( optionValue, this._resizeUnit ) );

            this.listenTo( button, 'execute', () => {
                editor.execute( 'resizeRecorder', { width: optionValue } );
            } );

            return button;
        } );
    }

    _registerRecorderResizeDropdown( options ) {
        const editor = this.editor;
        const t = editor.t;
        const originalSizeOption = options.find( option => !option.value );

        const componentCreator = locale => {
            const command = editor.commands.get( 'resizeRecorder' );
            const dropdownView = createDropdown( locale, DropdownButtonView );
            const dropdownButton = dropdownView.buttonView;

            dropdownButton.set( {
                tooltip: t( 'Resize recorder' ),
                commandValue: originalSizeOption.value,
                icon: RESIZE_ICONS.medium,
                isToggleable: true,
                label: this._getOptionLabelValue( originalSizeOption ),
                withText: true,
                class: 'ck-resize-recorder-button'
            } );

            dropdownButton.bind( 'label' ).to( command, 'value', commandValue => {
                if ( commandValue && commandValue.width ) {
                    return commandValue.width;
                } else {
                    return this._getOptionLabelValue( originalSizeOption );
                }
            } );

            dropdownView.bind( 'isEnabled' ).to( this );

            addListToDropdown(
                dropdownView,
                () => this._getResizeDropdownListItemDefinitions( options, command ),
                {
                    ariaLabel: t( 'Recorder resize list' ),
                    role: 'menu'
                }
            );

            this.listenTo( dropdownView, 'execute', evt => {
                editor.execute( evt.source.commandName, { width: evt.source.commandValue } );
                editor.editing.view.focus();
            } );

            return dropdownView;
        };

        editor.ui.componentFactory.add( 'resizeRecorder', componentCreator );
        editor.ui.componentFactory.add( 'recorderResize', componentCreator );
    }

    _getOptionLabelValue( option, forTooltip = false ) {
        const t = this.editor.t;

        if ( option.label ) {
            return option.label;
        } else if ( forTooltip ) {
            if ( option.value ) {
                return t( 'Resize recorder to %0', option.value + this._resizeUnit );
            } else {
                return t( 'Resize recorder to the original size' );
            }
        } else {
            if ( option.value ) {
                return option.value + this._resizeUnit;
            } else {
                return t( 'Original' );
            }
        }
    }

    _getResizeDropdownListItemDefinitions( options, command ) {
        const itemDefinitions = new Collection();

        options.map( option => {
            // Aquí también, solo el número
            const optionValue = option.value ? option.value : null;

            const definition = {
                type: 'button',
                model: new Model( {
                    commandName: 'resizeRecorder',
                    commandValue: optionValue,
                    label: this._getOptionLabelValue( option ),
                    withText: true,
                    icon: null
                } )
            };

            definition.model.bind( 'isOn' ).to(
                command,
                'value',
                getIsOnButtonCallback( optionValue, this._resizeUnit )
            );

            itemDefinitions.add( definition );
        } );

        return itemDefinitions;
    }
}

function getIsOnButtonCallback( value, unit ) {
    return commandValue => {
        const objectCommandValue = commandValue;

        if ( value === null && objectCommandValue === value ) {
            return true;
        }

        return objectCommandValue !== null && objectCommandValue.width === (value ? value + unit : null);
    };
}
